import React, { FC, PropsWithChildren, useEffect, useMemo } from 'react';
import { Col, Form, Row } from 'antd';
import classNames from 'classnames';
import { SectorEquipmentType } from '@models/enums';
import { EditableComponentProps, InformationProps } from '@models/interfaces';
import { ScopingSectorsVersionData } from '@models/types';
import { Select } from '@shared/modules';
import { PermittingSectorEquipment } from '../sector-equipment';

import './styles.scss';

type SectorsOfEquipmentProps = InformationProps &
  EditableComponentProps & {
    sectorsDataVersions: ScopingSectorsVersionData[];
  };

export const PermittingSectorsOfEquipment: FC<SectorsOfEquipmentProps> = ({
  className,
  isEditing,
  sectorsDataVersions,
}: PropsWithChildren<SectorsOfEquipmentProps>) => {
  const [versionForm] = Form.useForm();

  const selectedVersion = useMemo(
    () =>
      sectorsDataVersions.find(
        sectorsDataVersion => sectorsDataVersion.isSelected,
      )!,
    [sectorsDataVersions],
  );

  useEffect(() => {
    versionForm.setFieldsValue({ version: selectedVersion.version });
  }, [selectedVersion, versionForm]);

  const versionOptions = useMemo(
    () =>
      sectorsDataVersions.map(sectorsDataVersion => ({
        value: sectorsDataVersion.version,
        viewValue: sectorsDataVersion.isCurrent
          ? `${sectorsDataVersion.version} (Current)`
          : sectorsDataVersion.version,
      }))!,
    [sectorsDataVersions],
  );

  const currentSectors = useMemo(
    () => Object.keys(selectedVersion.scopingSectors) as SectorEquipmentType[],
    [selectedVersion],
  );

  const filteredSelectors = useMemo(
    () =>
      currentSectors.map((sector: SectorEquipmentType) => (
        <PermittingSectorEquipment
          type={sector}
          key={sector}
          className={className}
          isEditing={isEditing}
          data={selectedVersion.scopingSectors}
        />
      )),
    [currentSectors, isEditing, sectorsDataVersions],
  );

  return (
    <div className={classNames('prov-equipment-sectors', className)}>
      <Row className="prov-equipment-sectors__version" justify="space-between">
        <Col span={6}>
          <Form
            initialValues={{
              version: selectedVersion.version,
            }}
            form={versionForm}
          >
            <Select
              id="version"
              label="Version"
              options={versionOptions}
              isEditing={isEditing}
            />
          </Form>
        </Col>
      </Row>
      {filteredSelectors}
    </div>
  );
};
